import { useSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import Card from '@mui/material/Card';
import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import { Grid, List, Alert, ListItem, Skeleton, CardHeader, Typography, CardContent } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { EmailConnectionStatus } from 'src/services/email-sync/email-sync.types';
import { useGetMailerSettingsQuery, useGetEmailConnectionStateQuery, useUpdateMailerSettingsMutation, useInitiateEmailConnectionMutation, useDisconnectEmailConnectionMutation } from 'src/services/email-sync/email-sync.service';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import FeatureLockedButton from 'src/components/billing/feature-locked-button';
import EmailSignatureEditor from 'src/components/email-signature-editor/email-signature-editor';

import { FeatureType } from 'src/types/subscription.types';

import { useFeatures } from '../billing/usage/hooks/use-features';
import EmailTemplateManager from './components/email-template-manager';

// ----------------------------------------------------------------------

export default function AccountEmailConnection() {

  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate();

  const { canAccess } = useFeatures();

  const disconnectConfirm = useBoolean();

  const [redirectLoading, setRedirectLoading] = useState(true);
  const [emailSignature, setEmailSignature] = useState("");

  const { currentData: connectionState, isLoading, isError } = useGetEmailConnectionStateQuery();
  const { currentData: mailerSettings } = useGetMailerSettingsQuery();

  const [initiateEmailConnection] = useInitiateEmailConnectionMutation();
  const [updateMailerSettings, { isLoading: isUpdatingSignature }] = useUpdateMailerSettingsMutation();

  const [disconnectEmailConnection, { isLoading: isDisconnecting }] = useDisconnectEmailConnectionMutation();

  useEffect(() => {
    setRedirectLoading(false);
  }, [connectionState]);

  useEffect(() => {
    if (mailerSettings?.signature) {
      setEmailSignature(mailerSettings.signature);
    }
  }, [mailerSettings]);

  const handleInitiateConnect = async () => {
    try {
      setRedirectLoading(true);
      const response = await initiateEmailConnection().unwrap();
      window.location.href = response.url;

    } catch (error) {
      setRedirectLoading(false);
      enqueueSnackbar(t('account.email-connection.api.connect.default_error'), { variant: 'error' });
    }
  };

  const handleDisconnect = async () => {
    try {

      await disconnectEmailConnection().unwrap();
      enqueueSnackbar(t('account.email-connection.api.disconnect.success'), { variant: 'success' });
      disconnectConfirm.onFalse();

    } catch (error) {
      enqueueSnackbar(t('account.email-connection.api.disconnect.default_error'), { variant: 'error' });
    }
  };

  const handleSignatureUpdate = async () => {
    try {
      await updateMailerSettings({ signature: emailSignature }).unwrap();
      enqueueSnackbar(t('account.email-connection.api.update_signature.success'), { variant: 'success' });

    }
    catch (error) {
      enqueueSnackbar(t('account.email-connection.api.update_signature.default_error'), { variant: 'error' });
    }
  }

  const emailFeatureEnabled = useMemo(() => canAccess(FeatureType.EMAIL_CONNECTOR_ENABLED), [canAccess]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader title={t('account.email-connection.title')} />
        <CardContent>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="50%" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader title={t('account.email-connection.title')} />
        <CardContent>
          <Typography variant="body2" textAlign="center" color="text.secondary">
            {t('account.email-connection.error')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={3}>

      {/* Email connection status */}
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={t('account.email-connection.title')}
            subheader={t('account.email-connection.subtitle')}
          />
          <CardContent sx={{ py: 1 }}>

            {
              connectionState?.status === EmailConnectionStatus.ACTIVE && (
                <Stack>
                  <Stack direction="column" spacing={1}>

                    {/* email settings (signatures, templates, etc.) */}

                    <Alert severity="success" sx={{ maskType: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('account.email-connection.connected_alert')}
                      </Typography>
                    </Alert>
                  </Stack>
                </Stack>
              )
            }

            {
              connectionState?.status === EmailConnectionStatus.DISABLED && (
                <Stack>
                  <Stack direction="column" spacing={1}>

                    <Alert severity="info" sx={{ maskType: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('account.email-connection.disabled_alert')}
                      </Typography>
                    </Alert>

                  </Stack>

                </Stack>
              )
            }

            {
              connectionState?.status === EmailConnectionStatus.EXPIRED && (
                <Stack>
                  <Stack direction="column" spacing={1}>

                    {/* email settings (signatures, templates, etc.) */}

                    <Alert severity="warning" sx={{ maskType: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('account.email-connection.expired_alert')}
                      </Typography>
                    </Alert>

                    <LoadingButton
                      variant="contained"
                      onClick={handleInitiateConnect}
                      loading={redirectLoading}
                      startIcon={<Iconify icon="ic:twotone-refresh" />}
                      sx={{ mt: 2 }}
                    >
                      {t('account.email-connection.reauthorise')}
                    </LoadingButton>
                  </Stack>

                </Stack>
              )
            }

            {
              connectionState?.status === EmailConnectionStatus.DISCONNECTED && (
                <Stack>
                  <Stack direction="column" spacing={1}>
                    <Alert severity="info" sx={{ maskType: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('account.email-connection.not_connected_alert')}
                      </Typography>
                    </Alert>
                    <List>
                      <ListItem>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify icon="icon-park-solid:check-one" width={20} height={20} color="success.main" />
                          <Typography variant="body2" color="text.secondary">
                            {t('account.email-connection.info_list.item_1')}
                          </Typography>
                        </Stack>
                      </ListItem>
                      <ListItem>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify icon="icon-park-solid:check-one" width={20} height={20} color="success.main" />
                          <Typography variant="body2" color="text.secondary">
                            {t('account.email-connection.info_list.item_2')}
                          </Typography>
                        </Stack>
                      </ListItem>
                      <ListItem>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify icon="icon-park-solid:check-one" width={20} height={20} color="success.main" />
                          <Typography variant="body2" color="text.secondary">
                            {t('account.email-connection.info_list.item_3')}
                          </Typography>
                        </Stack>
                      </ListItem>
                      <ListItem>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify icon="icon-park-solid:check-one" width={20} height={20} color="success.main" />
                          <Typography variant="body2" color="text.secondary">
                            {t('account.email-connection.info_list.item_4')}
                          </Typography>
                        </Stack>
                      </ListItem>
                      <ListItem>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify icon="icon-park-solid:check-one" width={20} height={20} color="success.main" />
                          <Typography variant="body2" color="text.secondary">
                            {t('account.email-connection.info_list.item_5')}
                          </Typography>
                        </Stack>
                      </ListItem>
                    </List>

                    {
                      emailFeatureEnabled ? (
                        <LoadingButton
                          variant="contained"
                          onClick={handleInitiateConnect}
                          startIcon={<Iconify icon="mdi:email" />}
                          loading={redirectLoading}
                          sx={{ mt: 2 }}
                        >
                          {t('account.email-connection.connect')}
                        </LoadingButton>
                      ) :
                        <FeatureLockedButton
                          color='warning'
                          label={t(`common.subscription_required`)}
                          variant="contained"
                        />
                    }

                  </Stack>
                </Stack>
              )
            }

          </CardContent>
        </Card>
      </Grid>

      {/* Email signatures */}
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={t('account.email-connection.signatures.title')}
            subheader={t('account.email-connection.signatures.subtitle')}
          />
          <CardContent>
            {/* Email signature editor */}
            <Stack spacing={3}>

              {/* <div dangerouslySetInnerHTML={{ __html: mailerSettings?.signature ?? "" }} style={{ whiteSpace: 'pre' }} /> */}

              <EmailSignatureEditor onChange={(html) => setEmailSignature(html)} html={mailerSettings?.signature ?? ""} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  variant="contained"
                  startIcon={<Iconify icon="eva:save-fill" />}
                  onClick={handleSignatureUpdate}
                  loading={isUpdatingSignature}
                >
                  {t('common.save', 'Save')}
                </LoadingButton>
              </Box>

            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* Email message template */}
      <Grid item xs={12}>
        <EmailTemplateManager />
      </Grid>

      {/* Danger zone */}
      {[EmailConnectionStatus.ACTIVE, EmailConnectionStatus.EXPIRED].includes(connectionState?.status as EmailConnectionStatus) && (
        <Grid item xs={12}>
          <Card sx={{ py: 2, px: 4 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">{t('account.email-connection.danger_zone.title')}</Typography>
              <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', my: 4 }}>
                <Stack direction="column">
                  <Typography variant='h6'>{t('account.email-connection.danger_zone.disconnect')}</Typography>
                  <Typography variant='caption'>{t('account.email-connection.danger_zone.subtitle')}</Typography>
                </Stack>
                <LoadingButton
                  variant="contained"
                  color="error"
                  onClick={disconnectConfirm.onTrue}
                  startIcon={<Iconify icon="fluent:plug-disconnected-16-filled" />}
                  loading={isDisconnecting}
                  sx={{ mt: 2 }}
                >
                  {t('account.email-connection.disconnect')}
                </LoadingButton>
              </Stack>
            </Box>
          </Card>
        </Grid>
      )}

      <ConfirmDialog
        open={disconnectConfirm.value}
        onClose={disconnectConfirm.onFalse}
        title={t('account.email-connection.disconnect')}
        content={t('account.email-connection.disconnect_confirm')}
        action={
          <LoadingButton variant="contained" color="error" onClick={handleDisconnect} loading={isDisconnecting} >
            {t('account.email-connection.disconnect')}
          </LoadingButton>
        }
      />
    </Grid>
  );
}
